import { FC, useCallback, useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import { Flex, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import type { AnimationEffect } from '@adflow/types';
import { useStore } from '../../../hooks/useStore';
import {
  DuplicatePresetError,
  getAnimationPreset,
  type AnimationPresetPhase,
} from '../../../animation';
import theme from '@adflow/theme';

import { PresetPicker } from './PresetPicker';
import { AppliedEffectEditor } from './AppliedEffectEditor';

const phaseTabStyles = {
  color: 'gray.500',
  fontWeight: 700,
  fontSize: 'sm',
  letterSpacing: '0.04em',
  px: 3,
  py: 1,
  mb: '-2px',
  borderBottom: '2px solid',
  borderColor: 'transparent',
  _selected: {
    color: theme.palette.mcBlue.main,
    borderColor: theme.palette.mcBlue.main,
  },
  _hover: {
    color: theme.palette.mcBlue.main,
  },
  _focus: { boxShadow: 'none' },
  _focusVisible: { boxShadow: 'outline' },
};

type Props = {
  targetId: string;
};

function effectsForPhase(
  effects: AnimationEffect[] | undefined,
  targetId: string,
  phase: AnimationPresetPhase,
): AnimationEffect[] {
  return (effects ?? []).filter((effect) => {
    if (effect.targetId !== targetId) {
      return false;
    }
    try {
      return getAnimationPreset(effect.presetId).phase === phase;
    } catch {
      return false;
    }
  });
}

const AnimateControls: FC<Props> = ({ targetId }) => {
  const store = useStore(
    (state) => ({
      animation: state.animation,
      applyAnimationEffect: state.applyAnimationEffect,
      updateAnimationEffect: state.updateAnimationEffect,
      removeAnimationEffect: state.removeAnimationEffect,
    }),
    shallow,
  );

  const effects = store.animation?.effects;
  const appliedPresetIds = useMemo(
    () =>
      new Set(
        (effects ?? [])
          .filter((effect) => effect.targetId === targetId)
          .map((effect) => effect.presetId),
      ),
    [effects, targetId],
  );

  const handleApply = useCallback(
    (presetId: string) => {
      if (appliedPresetIds.has(presetId)) {
        return;
      }
      try {
        store.applyAnimationEffect(targetId, presetId);
      } catch (error) {
        if (error instanceof DuplicatePresetError) {
          return;
        }
        throw error;
      }
    },
    [appliedPresetIds, store, targetId],
  );

  const renderPhasePanel = (phase: AnimationPresetPhase) => {
    const phaseEffects = effectsForPhase(effects, targetId, phase);
    return (
      <Flex direction="column" gap={5}>
        {phaseEffects.map((effect) => (
          <AppliedEffectEditor
            key={effect.id}
            effect={effect}
            onUpdate={store.updateAnimationEffect}
            onRemove={store.removeAnimationEffect}
          />
        ))}
        <PresetPicker phase={phase} appliedPresetIds={appliedPresetIds} onApply={handleApply} />
      </Flex>
    );
  };

  return (
    <Tabs variant="unstyled" isLazy>
      <TabList borderBottomWidth="1px" borderColor="gray.200" data-test-id="animate-phase-tabs">
        <Tab {...phaseTabStyles} data-test-id="animate-in-tab">
          IN
        </Tab>
        <Tab {...phaseTabStyles} data-test-id="animate-out-tab">
          OUT
        </Tab>
      </TabList>
      <TabPanels>
        <TabPanel px={0} pt={4} pb={0} data-test-id="animate-in-panel">
          {renderPhasePanel('IN')}
        </TabPanel>
        <TabPanel px={0} pt={4} pb={0} data-test-id="animate-out-panel">
          {renderPhasePanel('OUT')}
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

AnimateControls.displayName = 'AnimateControls';

export default AnimateControls;
